import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';

/**
 * Horizontal review-cards carousel below the product grid.
 *
 * Renders nothing when the store has no reviews — the section
 * shouldn't take up space (or imply "no reviews yet" copy) on stores
 * that simply haven't gathered any. Fetch errors fail silently for
 * the same reason: a broken endpoint should not leave a half-baked
 * carousel on the homepage.
 *
 * The avatar gradients are deliberately *not* themed — they're
 * decorative variety, randomized per-card by index. Themed avatars
 * would all look identical, which is worse for a review wall.
 */

const CARD_RADIUS = '12px';
const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.06)';
const SECTION_MAX_WIDTH = '1200px';
const SECTION_HPAD = '1.5rem';

const QuoteIcon = ({ size = 24, color = '#e5e7eb' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none" aria-hidden="true">
    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11H10v10H0z" />
  </svg>
);

const StarIcon = ({ size = 16, filled = true, color = '#f59e0b' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const SectionHeader = ({ title, subtitle, themeStyles, theme }) => (
  <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
    <h2 style={{
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: '700',
      color: themeStyles?.text || '#111827',
      marginBottom: '0.5rem',
      fontFamily: theme?.fonts?.heading || 'inherit'
    }}>
      {title}
    </h2>
    <div style={{
      width: '40px',
      height: '3px',
      backgroundColor: themeStyles?.primary || '#2563eb',
      borderRadius: '2px',
      margin: '0 auto 0.75rem'
    }} />
    {subtitle && (
      <p style={{
        fontSize: '1rem',
        color: themeStyles?.textMuted || '#6b7280',
        maxWidth: '500px',
        margin: '0 auto'
      }}>
        {subtitle}
      </p>
    )}
  </div>
);

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #f093fb, #f5576c)',
  'linear-gradient(135deg, #4facfe, #00f2fe)',
  'linear-gradient(135deg, #43e97b, #38f9d7)',
  'linear-gradient(135deg, #fa709a, #fee140)'
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const ReviewsCarousel = ({ storeSlug, themeStyles, theme }) => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!storeSlug) return;
    let cancelled = false;
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`/store-reviews/store/${storeSlug}`);
        if (cancelled) return;
        if (response.data && Array.isArray(response.data)) {
          setReviews(response.data);
        } else if (response.data?.reviews && Array.isArray(response.data.reviews)) {
          setReviews(response.data.reviews);
        }
      } catch (err) {
        // silently fail — see header comment
      }
    };
    fetchReviews();
    return () => { cancelled = true; };
  }, [storeSlug]);

  if (!reviews || reviews.length === 0) return null;

  return (
    <div style={{
      padding: '4rem 0',
      backgroundColor: themeStyles?.backgroundAlt || '#fafafa'
    }}>
      <div style={{ maxWidth: SECTION_MAX_WIDTH, margin: '0 auto', padding: `0 ${SECTION_HPAD}` }}>
        <SectionHeader
          title="What Our Customers Say"
          subtitle="Real feedback from real customers"
          themeStyles={themeStyles}
          theme={theme}
        />
        <div style={{
          display: 'flex',
          gap: '1.25rem',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: '0.5rem'
        }}>
          {reviews.map((review, idx) => (
            <div key={review.id || review._id || idx} style={{
              minWidth: '300px',
              maxWidth: '360px',
              flex: '0 0 auto',
              scrollSnapAlign: 'start',
              backgroundColor: themeStyles?.background || 'white',
              border: `1px solid ${themeStyles?.border || '#e5e7eb'}`,
              borderRadius: CARD_RADIUS,
              padding: '1.5rem',
              position: 'relative',
              boxShadow: CARD_SHADOW
            }}>
              <div style={{ marginBottom: '0.75rem', opacity: 0.3 }}>
                <QuoteIcon size={24} color={themeStyles?.textMuted || '#d1d5db'} />
              </div>
              <div style={{ display: 'flex', gap: '2px', marginBottom: '0.75rem' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <StarIcon key={star} size={16} filled={star <= (review.rating || 5)} color="#f59e0b" />
                ))}
              </div>
              <p style={{
                fontSize: '0.9375rem',
                color: themeStyles?.textMuted || '#4b5563',
                lineHeight: '1.6',
                fontStyle: 'italic',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                marginBottom: '1rem'
              }}>
                {review.reviewText || review.comment || review.text || review.review || ''}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: '700'
                }}>
                  {getInitials(review.customerName || review.name || review.author)}
                </div>
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: themeStyles?.text || '#111827'
                }}>
                  {review.customerName || review.name || review.author || 'Customer'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReviewsCarousel;
